import _ from 'lodash';
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { getProjectHistory, analysisDirectory, createProject } from '@/infrastructure/api-functions/create';
import { PatsnapCopilotService } from '@analytics/patsnap-copilot/src/patsnap-copilot-service.interface';
import { injectService } from '@patsnap/tahiti-core/ioc/inject';
import type { ProjectCopilotServiceImpl } from '@/services/project-copilot-service';
import type { GenerateLoadingProcess, IAnalysisDirectory } from '@/types';
import { create } from '@eureka/utils/deferred';

const directory = ref<IAnalysisDirectory[]>([]);
const customizeDirectory = ref<IAnalysisDirectory[]>([]);
const stepContent = ref<GenerateLoadingProcess[]>([]);
const isAnalyzing = ref(false);
const displayQuestion = ref('');
const currentStep = ref(0);
const generateDeferred = create<void>();

export function useStep() {
    const { t } = useI18n();
    const route = useRoute();
    const copilotService = injectService<PatsnapCopilotService>(PatsnapCopilotService) as unknown as ProjectCopilotServiceImpl;
    const getProgress = () => [{
        step: t('chat.stepGenerate.title1'),
        content: displayQuestion.value,
        haveFinish: false,
    }, {
        step: t('chat.stepGenerate.title2'),
        content: t('chat.stepGenerate.content2'),
        haveFinish: false,
    }, {
        step: t('chat.stepGenerate.title3'),
        content: t('chat.stepGenerate.content3'),
        haveFinish: false,
    }]

    const isProjectFinished = computed(() => !!route.params.projectId)
    const updateStatus = async(projectId?: string, question?: string) => {
        displayQuestion.value = question || '';
        directory.value = [];
        customizeDirectory.value = [];
        stepContent.value = [];
        currentStep.value = displayQuestion.value ? 1 : 0;
        const previousStep = currentStep.value;
        if (!projectId) {
            return;
        }
        const { modules } = await getProjectHistory(projectId, 'MODEL_ANALYSIS');
        const dirRecord = _.find(modules, { module_type: 'tech_research_directory' });
        const questionRecord = _.find(modules, { module_type: 'tech_research_question' });
        if (questionRecord && previousStep === currentStep.value) {
            try {
                displayQuestion.value = JSON.parse(_.get(questionRecord, 'records.0.output', '{}')).current_question;
                currentStep.value = 1;
            } catch (e) {
                // ignore
            }
        }
        if (dirRecord) {
            const allDirections = JSON.parse(_.get(dirRecord, 'records.0.output', '{}')).directory;
            directory.value = _.filter(allDirections, item => item.source_type === 'model');
            customizeDirectory.value = _.filter(allDirections, item => item.source_type === 'customize');
            stepContent.value = _.map(getProgress(), item => ({
                ...item,
                haveFinish: true,
            }));
            currentStep.value = 3;
        }
    }

    const startProgress = async function() {
        let step = 0;
        const progress = getProgress();
        const t = setInterval(() => {
            if (step < progress.length) {
                stepContent.value = [
                    ..._.map(stepContent.value, item => {
                        return {
                            ...item,
                            haveFinish: true,
                        }
                    }),
                    progress[step++]
                ]
            } else {
                clearInterval(t);
            }
        }, 300);
        await generateDeferred.promise;
        const steps = stepContent.value;
        if (steps.length) {
            steps[steps.length - 1].haveFinish = true;
            stepContent.value = [...steps];
        }
    }

    const create = function(directoryList: IAnalysisDirectory[]) {
        currentStep.value = 3;
        startProgress();
        return analysisDirectory(copilotService.jobId || '', copilotService.lastInput, directoryList)
            .then(() => createProject(copilotService.jobId || ''))
            .then((project) => {
                generateDeferred.resolve();
                return project;
            }).catch(e => {
                currentStep.value = 2;
                throw e;
            })
    }

    const analysis = function() {
        isAnalyzing.value = true;
        currentStep.value = 2;
        return analysisDirectory(copilotService.jobId || '', copilotService.lastInput).then(data => {
            [directory.value, customizeDirectory.value] = _.partition(data, item => item.source_type === 'model');
        }).finally(() => {
            isAnalyzing.value = false;
        });
    }

    const isStreamProcessing = computed(() => {
        return _.some(stepContent.value, item => !item.haveFinish) || !stepContent.value.length;
    })

    return {
        currentStep,
        displayQuestion,
        stepContent,
        updateStatus,
        analysis,
        directory,
        customizeDirectory,
        create,
        isAnalyzing,
        isProjectFinished,
        isStreamProcessing,
    }
}
